import { UsageCostCell, UsageTotals, formatUsageDuration, formatUsageTokens, formatUsageCost } from '../components/LlmUsage';
/**
 * UserThreadObserver Component
 * Enhanced observer dashboard with user/MSP selection and thread view.
 * Tabs: AI Thread Observer | RAG Observer | Activity Report | Usage Quality.
 * RAG: ?tab=rag, activity: ?tab=activity (legacy ?tab=digest), quality: ?tab=quality.
 * Page structure: Container, Tabs, RagObserverPanel or thread filters + table.
 * Thread filters: one flex row (wraps on narrow screens), min messages narrow column; client-side message count filter.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Link as MuiLink,
} from '@mui/material';
import {
  Sms as SmsIcon,
  OpenInNew as OpenInNewIcon,
  Psychology as PsychologyIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import ObserverApi, {
  ThreadUsage,
  LlmUsageSummary,
  ObserverChatMessagePayload,
} from '../apis/observerApi';
import {
  OBSERVER_QUERY_ERROR_CODES,
  readObserverApiErrorCode,
} from '../constants/observerQueryErrors';
import { formatObserverTimestamp } from '../utils/threadUtils';
import ChatBubble from '../../components/ChatBubble';
import {
  extractResearchJobId,
  buildReportHref,
  ResearchReportJobStatusInline,
} from '../../shared/components/ResearchReportJobStatusInline';
import ToolAuditLogPopup from '../../components/ToolAuditLogPopup';
import RagObserverPanel from '../components/RagObserverPanel';
import ActivityReportPanel from '../components/ActivityReportPanel';
import TheoUsageQualityPanel from '../components/TheoUsageQualityPanel';
import ObserverScopeFilters from '../components/ObserverScopeFilters';
import { useObserverScope } from '../hooks/useObserverScope';
import {
  collapseStreamingChunks,
  groupConsecutivePlanSteps,
  normalizeMessages,
} from '../../shared/utils/messageUtils';
import type { ChatMessage } from '../../shared/utils/messageUtils';
import { cleanContent, formatTimestamp } from '../../utils/chatUtils';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useUserMsp } from '../../hooks/useUserMsp';
import { canAccessAnalyticsAssistant } from '../../utils/analyticsAssistantAccess';
import {
  observerThreadUserExperienceTitle,
  observerThreadUserExperienceUrl,
  type ObserverThreadActionRow,
} from '../utils/observerThreadActionRow';
import {
  parseReportAttachmentsFromContent,
  stripReportAttachmentTokensForDisplay,
} from '../../shared/utils/reportAttachmentTokens';
import {
  AUTOMATION_AGENT_DISPLAY_NAME,
  agentDisplayNameForMessage,
  isAutomationActorUserId,
  isNeoAssistantAuthor,
  THEO_ASSISTANT_DISPLAY_NAME,
} from '../../shared/utils/messageSenderLabels';

interface MSP {
  id: number;
  name: string;
}

type UserThread = ObserverThreadActionRow & { usage: LlmUsageSummary };

interface Message {
  message_id: number;
  ai_thread_id: number;
  user_id: string | null;
  content_type: 'MESSAGE' | 'HUMAN_REVIEW';
  content: string;
  created_at: string;
  langsmith_run_id?: string;
}

const toChatTimestamp = (value: string) => {
  const parsedTime = Date.parse(value);
  if (!Number.isFinite(parsedTime)) {
    return { seconds: '0', nanos: 0 };
  }

  const seconds = Math.floor(parsedTime / 1000);
  const nanos = (parsedTime % 1000) * 1_000_000;
  return {
    seconds: String(seconds),
    nanos,
  };
};

const toLocalDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultEndDate = () => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return end;
};

const getDefaultStartDate = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 6);
  return start;
};

const isValidDate = (date: Date | null): date is Date =>
  Boolean(date && !Number.isNaN(date.getTime()));

const parseDateInput = (value: string): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Shown when start/end dates are reversed or the API rejects the range (grep: date range validation). */
const THREADS_DATE_RANGE_USER_MESSAGE =
  'Start date must be the same as or an earlier day than the end date.';

function getThreadsLoadErrorMessage(err: unknown): string {
  const code = readObserverApiErrorCode(err);
  if (code === OBSERVER_QUERY_ERROR_CODES.INVALID_THREAD_DATE_RANGE) {
    return THREADS_DATE_RANGE_USER_MESSAGE;
  }
  if (code === OBSERVER_QUERY_ERROR_CODES.INVALID_THREAD_DATE) {
    return 'Use valid calendar dates for start and end, or leave a field empty.';
  }
  if (err instanceof Error && err.message?.trim()) {
    return err.message;
  }
  return 'Could not load threads. Check your connection and try again.';
}

const UserThreadObserver: React.FC = () => {
  const flags = useFeatureFlag();
  const { userMspInfo } = useUserMsp();
  const canUseDeepResearch = canAccessAnalyticsAssistant(flags as Record<string, unknown>, userMspInfo?.role);
  const [msps, setMSPs] = useState<MSP[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(() => getDefaultStartDate());
  const [endDate, setEndDate] = useState<Date | null>(() => getDefaultEndDate());
  const [threads, setThreads] = useState<UserThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState<string | null>(null);
  const [threadsError, setThreadsError] = useState<string | null>(null);
  const [threadMessagesError, setThreadMessagesError] = useState<string | null>(null);
  const [selectedThread, setSelectedThread] = useState<ObserverThreadActionRow | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatMessages, setChatMessages] = useState<ObserverChatMessagePayload[]>([]);
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [messagesViewTab, setMessagesViewTab] = useState<'chat' | 'messages'>('chat');
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [auditLogPopupOpen, setAuditLogPopupOpen] = useState(false);
  const [selectedAuditThread, setSelectedAuditThread] = useState<ObserverThreadActionRow | null>(null);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [selectedUsageThread, setSelectedUsageThread] = useState<ObserverThreadActionRow | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<ThreadUsage | null>(null);
  /** Minimum `message_count` for a thread to appear in the table (0 = no floor). */
  const [minMessageCount, setMinMessageCount] = useState(0);
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const observerTab: 'threads' | 'rag' | 'activity' | 'quality' =
    tabParam === 'rag'
      ? 'rag'
      : tabParam === 'activity' || tabParam === 'digest'
        ? 'activity'
        : tabParam === 'quality'
          ? 'quality'
          : 'threads';

  const handleCloseMessagesDialog = useCallback(() => {
    setMessagesDialogOpen(false);
    setMessagesViewTab('chat');
    setThreadMessagesError(null);
  }, []);

  // Sorting state
  const [sortField, setSortField] = useState<string>('last_message_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const api = useMemo(() => new ObserverApi(), []);
  const {
    selectedMspIds,
    selectedUserIds,
    users,
    usersLoading,
    usersFetchError,
    setUsersFetchError,
    mspIdsForRequest,
    userIdsForRequest,
    omitInternalUsers,
    mspScope,
    userScope,
    showMspColumn,
    handleMspChange,
    handleUserChange,
    setOmitInternalUsers,
  } = useObserverScope(msps, api, { fetchUsers: observerTab === 'threads' });

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setInitialLoadError(null);

        const mspsData = await api.getMSPs();
        setMSPs(mspsData);
      } catch (err) {
        setInitialLoadError(
          err instanceof Error ? err.message : 'Could not load MSP list. Refresh the page or try again later.',
        );
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [api]);

  const loadUserThreads = useCallback(async () => {
    if (mspIdsForRequest.length === 0 || userIdsForRequest === null) {
      setThreads([]);
      return;
    }

    setThreadsError(null);
    setThreadMessagesError(null);

    if (isValidDate(startDate) && isValidDate(endDate)) {
      const startDay = new Date(startDate);
      startDay.setHours(0, 0, 0, 0);
      const endDay = new Date(endDate);
      endDay.setHours(0, 0, 0, 0);
      if (startDay.getTime() > endDay.getTime()) {
        setThreads([]);
        setThreadsError(THREADS_DATE_RANGE_USER_MESSAGE);
        return;
      }
    }

    try {
      setThreadsLoading(true);

      const result = await api.getUserThreads({
        mspIds: mspIdsForRequest,
        userIds: userIdsForRequest,
        startDate: isValidDate(startDate) ? toLocalDateString(startDate) : undefined,
        endDate: isValidDate(endDate) ? toLocalDateString(endDate) : undefined,
      });

      setThreads(result.threads);
    } catch (err) {
      setThreads([]);
      setThreadsError(getThreadsLoadErrorMessage(err));
    } finally {
      setThreadsLoading(false);
    }
  }, [mspIdsForRequest, userIdsForRequest, startDate, endDate, api]);

  // Load threads when on the threads tab and scope or dates change
  useEffect(() => {
    if (observerTab !== 'threads') {
      return;
    }
    if (mspIdsForRequest.length > 0 && userIdsForRequest !== null) {
      loadUserThreads();
    } else {
      setThreads([]);
    }
  }, [
    observerTab,
    selectedUserIds,
    selectedMspIds,
    loadUserThreads,
    mspIdsForRequest.length,
    userIdsForRequest,
  ]);

  const mspScopeLabel = useMemo(() => {
    if (mspScope.mode === 'none') {
      return 'No MSPs selected';
    }
    if (mspScope.mode === 'all') {
      return 'All MSPs';
    }
    if (mspIdsForRequest.length === 1) {
      return msps.find((msp) => msp.id === mspIdsForRequest[0])?.name
        ?? `MSP ${mspIdsForRequest[0]}`;
    }
    return `${mspIdsForRequest.length} MSPs`;
  }, [msps, mspScope.mode, mspIdsForRequest]);

  const userScopeLabel = useMemo(() => {
    if (userScope.mode === 'none') {
      return 'No users selected';
    }
    if (userScope.mode === 'all') {
      return null;
    }
    if (userIdsForRequest?.length === 1) {
      return userIdsForRequest[0];
    }
    return `${userIdsForRequest?.length ?? 0} users`;
  }, [userScope.mode, userIdsForRequest]);

  const mspNameById = useMemo(
    () => new Map(msps.map((msp) => [msp.id, msp.name])),
    [msps],
  );

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const effectiveMinMessageCount = React.useMemo(
    () => Math.max(0, Math.floor(Number(minMessageCount)) || 0),
    [minMessageCount],
  );

  const filteredThreads = React.useMemo(
    () =>
      threads.filter(
        (t) => Number(t.message_count ?? 0) >= effectiveMinMessageCount,
      ),
    [threads, effectiveMinMessageCount],
  );

  // Sort threads based on current sort field and order (after min-message filter)
  const sortedThreads = React.useMemo(() => {
    return [...filteredThreads].sort((a, b) => {
      const aValue = a[sortField as keyof UserThread];
      const bValue = b[sortField as keyof UserThread];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortOrder === 'asc' ? (aValue === bValue ? 0 : aValue ? 1 : -1) : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      return 0;
    });
  }, [filteredThreads, sortField, sortOrder]);

  const handleViewMessages = async (thread: ObserverThreadActionRow) => {
    try {
      setMessagesLoading(true);
      setThreadMessagesError(null);
      setSelectedThread(thread);

      const result = await api.getThreadDetail(thread.ai_thread_id);
      setMessages(result.messages);
      setChatMessages(result.chat_messages || []);
      setMessagesViewTab('chat');
      setMessagesDialogOpen(true);
    } catch (err) {
      setThreadMessagesError(
        err instanceof Error ? err.message : 'Could not load messages for this thread.',
      );
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleViewAuditLogs = (thread: ObserverThreadActionRow) => {
    setSelectedAuditThread(thread);
    setAuditLogPopupOpen(true);
  };

  const handleViewUsage = async (thread: ObserverThreadActionRow) => {
    try {
      setSelectedUsageThread(thread);
      setUsageDialogOpen(true);
      setUsageLoading(true);
      setUsageError(null);
      setUsageData(null);

      const result = await api.getThreadUsage(thread.ai_thread_id);
      setUsageData(result);
    } catch (err) {
      setUsageError(err instanceof Error ? err.message : 'Failed to load usage');
    } finally {
      setUsageLoading(false);
    }
  };


  const formatThreadTimestamp = (thread: UserThread) => {
    const timestamp = thread.last_message_at || thread.created_at;
    return timestamp ? formatObserverTimestamp(timestamp) : 'N/A';
  };

  const formatMessageTimestamp = (dateString: string) => {
    return formatObserverTimestamp(dateString);
  };

  const observerChatMessages = useMemo(() => {
    const baseMessages: ChatMessage[] = chatMessages.map((message) => ({
      message_id: String(message.message_id),
      ai_thread_id: String(message.ai_thread_id),
      user_id: message.user_id ?? '',
      content: message.content ?? '',
      content_type: message.content_type,
      created_at: toChatTimestamp(message.created_at),
      human_review: message.human_review,
      human_review_status: message.human_review_status,
      human_review_response: message.human_review_response,
      human_review_ack: message.human_review_ack,
      agent_display_name: message.agent_display_name,
      isHistory: true,
    }));

    return groupConsecutivePlanSteps(
      collapseStreamingChunks(
        normalizeMessages(baseMessages).map((message) => ({
          ...message,
          isHistory: true,
        })),
      ),
    );
  }, [chatMessages]);

  const getStatusColor = (isDeleted: boolean) => {
    return isDeleted ? 'error' : 'success';
  };

  const getStatusText = (isDeleted: boolean) => {
    return isDeleted ? 'Deleted' : 'Active';
  };

  const renderRawMessage = (message: Message) => (
    <Paper
      key={`raw-${message.message_id}`}
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        borderColor: '#E2E8F0',
        bgcolor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: '#19213D', fontWeight: 600 }}>
          {message.user_id || 'neo'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={message.content_type}
            size="small"
            sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 600 }}
          />
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            {formatMessageTimestamp(message.created_at)}
          </Typography>
        </Box>
      </Box>

      <Typography
        component="pre"
        sx={{
          m: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '0.875rem',
          lineHeight: 1.6,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          color: '#334155',
        }}
      >
        {message.content || '(empty)'}
      </Typography>
    </Paper>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Observer
      </Typography>

      {observerTab === 'rag' ? (
        <RagObserverPanel msps={msps} />
      ) : observerTab === 'activity' ? (
        <ActivityReportPanel
          msps={msps}
          threadActionHandlers={{
            onViewMessages: handleViewMessages,
            onViewAuditLogs: handleViewAuditLogs,
            onViewUsage: handleViewUsage,
          }}
        />
      ) : observerTab === 'quality' ? (
        <TheoUsageQualityPanel
          msps={msps}
          threadActionHandlers={{
            onViewMessages: handleViewMessages,
            onViewAuditLogs: handleViewAuditLogs,
            onViewUsage: handleViewUsage,
          }}
        />
      ) : (
        <>

      {initialLoadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {initialLoadError}
        </Alert>
      )}

      {/* MSP, user, dates, min messages — flex row wraps on narrow viewports */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'flex-start',
          mb: 4,
        }}
      >
        <ObserverScopeFilters
          msps={msps}
          users={users}
          selectedMspIds={selectedMspIds}
          selectedUserIds={selectedUserIds}
          onMspChange={handleMspChange}
          onUserChange={handleUserChange}
          omitInternalUsers={omitInternalUsers}
          onOmitInternalUsersChange={setOmitInternalUsers}
          usersLoading={usersLoading}
          mspLabel="MSP scope"
          userLabel="User scope"
        />
        <Box sx={{ flex: '1 1 130px', minWidth: 0, maxWidth: { xs: '100%', lg: '18%' } }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={isValidDate(startDate) ? toLocalDateString(startDate) : ''}
            onChange={(event) => setStartDate(parseDateInput(event.target.value))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box sx={{ flex: '1 1 130px', minWidth: 0, maxWidth: { xs: '100%', lg: '18%' } }}>
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={isValidDate(endDate) ? toLocalDateString(endDate) : ''}
            onChange={(event) => setEndDate(parseDateInput(event.target.value))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box
          sx={{
            flex: '0 1 160px',
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 160 },
            maxWidth: { xs: '100%' },
          }}
        >
          <TextField
            label="Min messages"
            type="number"
            size="small"
            value={minMessageCount}
            onChange={(event) => {
              const raw = event.target.value;
              if (raw === '') {
                setMinMessageCount(0);
                return;
              }
              const parsed = parseInt(raw, 10);
              if (!Number.isNaN(parsed) && parsed >= 0) {
                setMinMessageCount(parsed);
              }
            }}
            fullWidth
            inputProps={{ min: 0, step: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      {/* grep: observer error banners — user list / thread query / message preview failures */}
      {usersFetchError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setUsersFetchError(null)}
        >
          {usersFetchError}
        </Alert>
      )}
      {threadsError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setThreadsError(null)}
        >
          {threadsError}
        </Alert>
      )}
      {threadMessagesError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setThreadMessagesError(null)}
        >
          {threadMessagesError}
        </Alert>
      )}

      {/* Threads Table */}
      {mspScope.mode === 'none' ? (
        <Alert severity="info">Select at least one MSP to view threads.</Alert>
      ) : userScope.mode === 'none' ? (
        <Alert severity="info">Select at least one user to view threads.</Alert>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Threads for {mspScopeLabel}
              {userScopeLabel ? ` - ${userScopeLabel}` : ''}
              {isValidDate(startDate) && isValidDate(endDate) && ` (${toLocalDateString(startDate)} to ${toLocalDateString(endDate)})`}
            </Typography>
            
            {threadsLoading ? (
              <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Box>
            ) : threads.length === 0 ? (
              <Alert severity="info">No threads found for the selected scope.</Alert>
            ) : filteredThreads.length === 0 ? (
              <Alert severity="info">
                No threads have at least {effectiveMinMessageCount} message
                {effectiveMinMessageCount === 1 ? '' : 's'}. Lower &quot;Min messages&quot; or set
                it to 0 to see all threads.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        onClick={() => handleSort('ai_thread_id')}
                      >
                        Thread ID {sortField === 'ai_thread_id' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        onClick={() => handleSort('thread_name')}
                      >
                        Name {sortField === 'thread_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </TableCell>
                      {showMspColumn && (
                        <TableCell
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                          onClick={() => handleSort('msp_id')}
                        >
                          MSP {sortField === 'msp_id' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </TableCell>
                      )}
                      <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        onClick={() => handleSort('display_user_id')}
                      >
                        User email {sortField === 'display_user_id' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        onClick={() => handleSort('ticket_id')}
                      >
                        Ticket ID {sortField === 'ticket_id' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        onClick={() => handleSort('message_count')}
                      >
                        Messages {sortField === 'message_count' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </TableCell>
                      <TableCell 
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        onClick={() => handleSort('is_deleted')}
                      >
                        Status {sortField === 'is_deleted' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </TableCell>
                          <TableCell 
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                            onClick={() => handleSort('last_message_at')}
                          >
                            Timestamp {sortField === 'last_message_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </TableCell>
                          <TableCell align="right">Cost</TableCell>
                          <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedThreads.map((thread) => {
                      const userExperienceUrl = observerThreadUserExperienceUrl(thread);
                      const userExperienceTitle = observerThreadUserExperienceTitle(thread);
                      return (
                      <TableRow
                        key={thread.ai_thread_id}
                        onClick={() => handleViewMessages(thread)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <TableCell>{thread.ai_thread_id}</TableCell>
                        <TableCell>
                          {thread.thread_name || `Thread ${thread.ai_thread_id}`}
                        </TableCell>
                        {showMspColumn && (
                          <TableCell>
                            {thread.msp_id != null
                              ? mspNameById.get(Number(thread.msp_id)) ?? `MSP ${thread.msp_id}`
                              : 'N/A'}
                          </TableCell>
                        )}
                        <TableCell>{thread.display_user_id?.trim() || 'N/A'}</TableCell>
                        <TableCell>{thread.ticket_id || 'N/A'}</TableCell>
                        <TableCell>{thread.message_count}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(thread.is_deleted)}
                            color={getStatusColor(thread.is_deleted)}
                            size="small"
                          />
                        </TableCell>
                            <TableCell>{formatThreadTimestamp(thread)}</TableCell>
                            <UsageCostCell usage={thread.usage} onClick={() => { void handleViewUsage(thread); }} />
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {/* Read Messages Button */}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ minWidth: 'auto', px: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewMessages(thread);
                                  }}
                                  title="Read Messages"
                                >
                                  <SmsIcon fontSize="small" />
                                </Button>
                                
                                {/* Audit Logs Button */}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ minWidth: 'auto', px: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewAuditLogs(thread);
                                  }}
                                  title="View Audit Logs"
                                >
                                  <SecurityIcon fontSize="small" />
                                </Button>

                                {/* LangSmith Button */}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ minWidth: 'auto', px: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Link to LangSmith thread view
                                    const projectId = process.env.REACT_APP_LANGSMITH_PROJECT_ID || '20f0ccc5-d50a-46f8-8f72-3e8470aa7a3c';
                                    const langsmithUrl = `https://smith.langchain.com/o/44975c4f-7463-4174-a1b2-7390e12b2622/projects/p/${projectId}/t/${thread.ai_thread_id}`;
                                    window.open(langsmithUrl, '_blank');
                                  }}
                                  title="View in LangSmith"
                                >
                                  <PsychologyIcon fontSize="small" />
                                </Button>

                                {/* Open user-facing ticket/chat thread */}
                                {userExperienceUrl && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ minWidth: 'auto', px: 1 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(userExperienceUrl, '_blank');
                                    }}
                                    title={userExperienceTitle}
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Messages / usage / audit — mounted for AI Thread Observer, Activity Report, Usage Quality */}
      <Dialog
        open={messagesDialogOpen}
        onClose={handleCloseMessagesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Messages for Thread {selectedThread?.ai_thread_id}
          {selectedThread?.thread_name && ` - ${selectedThread.thread_name}`}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={messagesViewTab}
            onChange={(_, value: 'chat' | 'messages') => setMessagesViewTab(value)}
            sx={{ mb: 2, borderBottom: '1px solid #E2E8F0' }}
          >
            <Tab label="Chat" value="chat" />
            <Tab label="Messages" value="messages" />
          </Tabs>
          {messagesLoading ? (
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Box>
          ) : messagesViewTab === 'messages' ? (
            <Box
              sx={{
                maxHeight: '60vh',
                overflow: 'auto',
                pr: 0.5,
                bgcolor: '#F8FAFC',
                borderRadius: 2,
                p: 1.5,
              }}
            >
              {messages.length === 0 ? (
                <Typography color="text.secondary">No messages found for this thread.</Typography>
              ) : (
                messages.map(renderRawMessage)
              )}
            </Box>
          ) : (
            <Box
              sx={{
                maxHeight: '60vh',
                overflow: 'auto',
                px: 1,
                py: 2,
                bgcolor: '#F7F9FC',
                borderRadius: 2,
              }}
            >
              {observerChatMessages.length === 0 ? (
                <Typography color="text.secondary">No messages found for this thread.</Typography>
              ) : (
                observerChatMessages.map((message, index, array) => {
                  const isAssistant = isNeoAssistantAuthor(message.user_id);
                  const agentName = agentDisplayNameForMessage(array, index);
                  const displayName = agentName
                    || (isAssistant
                      ? THEO_ASSISTANT_DISPLAY_NAME
                      : isAutomationActorUserId(message.user_id)
                        ? AUTOMATION_AGENT_DISPLAY_NAME
                        : (message.user_id || 'User'));
                  const researchJobId = isAssistant
                    ? extractResearchJobId(message.content)
                    : null;
                  const observerThreadId = selectedThread?.ai_thread_id ?? null;
                  const reportAttachments = !isAssistant
                    ? parseReportAttachmentsFromContent(message.content)
                    : [];
                  const displayMessageContent = !isAssistant
                    ? stripReportAttachmentTokensForDisplay(message.content)
                    : String(message.content ?? '');

                  return (
                    <ChatBubble
                      key={`observer-${message.message_id || index}`}
                      sender={isAssistant || (Boolean(agentName) && isAutomationActorUserId(message.user_id)) ? 'assistant' : 'user'}
                      content={
                        <div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: cleanContent(displayMessageContent),
                            }}
                          />
                          {canUseDeepResearch && researchJobId && observerThreadId != null ? (
                            <ResearchReportJobStatusInline
                              jobId={researchJobId}
                              aiThreadId={observerThreadId}
                              pollEnabled
                            />
                          ) : null}
                        </div>
                      }
                      headerInlineContent={
                        reportAttachments.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {reportAttachments.map((attachment) => (
                              <MuiLink
                                key={`${message.message_id || 'msg'}-report-header-${attachment.jobId}`}
                                href={buildReportHref(attachment.jobId, null)}
                                target="_blank"
                                rel="noreferrer"
                                underline="none"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  border: '1px solid #D1D5DB',
                                  backgroundColor: '#F9FAFB',
                                  color: '#374151',
                                  borderRadius: '999px',
                                  px: 1,
                                  py: 0.2,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  fontFamily: 'var(--theo-font-sans)',
                                  lineHeight: 1.2,
                                  maxWidth: 420,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  '&:hover': {
                                    backgroundColor: '#F3F4F6',
                                  },
                                }}
                              >
                                {`report:${attachment.title}`}
                              </MuiLink>
                            ))}
                          </Box>
                        ) : null
                      }
                      timestamp={formatTimestamp(message.created_at?.seconds)}
                      senderName={displayName}
                      userId={displayName}
                      human_review={message.human_review}
                      human_review_status={message.human_review_status}
                      human_review_response={message.human_review_response}
                      isHistory
                      isLastMessage={index === array.length - 1}
                      messageId={message.message_id}
                      message={message}
                    />
                  );
                })
              )}
            </Box>
          )}
        </DialogContent>
      <DialogActions>
          <Button onClick={handleCloseMessagesDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={usageDialogOpen}
        onClose={() => setUsageDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Usage for Thread {selectedUsageThread?.ai_thread_id}
          {selectedUsageThread?.thread_name && ` - ${selectedUsageThread.thread_name}`}
        </DialogTitle>
        <DialogContent>
          {usageLoading ? (
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Box>
          ) : usageError ? (
            <Alert severity="error">{usageError}</Alert>
          ) : usageData ? (
            <Box>
              <UsageTotals usage={usageData} />
              {usageData.missing_turn_count > 0 && <Alert severity="info" sx={{ mb: 2 }}>
                {usageData.missing_turn_count} of {usageData.turns.length} turns have no recorded usage.
                Totals cover recorded API attempts only.
              </Alert>}
              {usageData.turns.length === 0 ? (
                <Alert severity="info">No recorded usage for this thread.</Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Turn</TableCell>
                        <TableCell>Feature</TableCell>
                        <TableCell>Timestamp</TableCell>
                        <TableCell align="right" title="Elapsed time from turn creation to completion, including queue time">Duration</TableCell>
                        <TableCell align="right">Input</TableCell>
                        <TableCell align="right">Output</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Cost</TableCell>
                        <TableCell align="right">API attempts</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usageData.turns.map((turn) => (
                        <TableRow key={turn.turn_id}>
                          <TableCell>{turn.turn_index}</TableCell>
                          <TableCell>{turn.feature || '—'}</TableCell>
                          <TableCell>{turn.start_time ? formatMessageTimestamp(turn.start_time) : 'N/A'}</TableCell>
                          <TableCell align="right">{formatUsageDuration(turn.elapsed_ms)}</TableCell>
                          <TableCell align="right">{formatUsageTokens(turn.input_tokens)}</TableCell>
                          <TableCell align="right">{formatUsageTokens(turn.output_tokens)}</TableCell>
                          <TableCell align="right">{formatUsageTokens(turn.total_tokens)}</TableCell>
                          <TableCell align="right">{formatUsageCost(turn.total_cost)}{turn.unpriced_count > 0 ? ' (partial)' : ''}</TableCell>
                          <TableCell align="right">{turn.attempt_count}</TableCell>
                          <TableCell>
                            <Chip
                              label={turn.attempt_count === 0 ? 'No recorded usage' : turn.failed_attempts > 0 ? `${turn.failed_attempts} failed attempts` : 'Completed'}
                              size="small"
                              color={turn.attempt_count === 0 ? 'default' : turn.failed_attempts > 0 ? 'error' : 'success'}
                            />
                          </TableCell>

                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : (
            <Alert severity="info">No usage data available.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ToolAuditLogPopup
        open={auditLogPopupOpen}
        onClose={() => setAuditLogPopupOpen(false)}
        aiThreadId={selectedAuditThread ? String(selectedAuditThread.ai_thread_id) : null}
      />
    </Container>
  );
};

export default UserThreadObserver;
